package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.data.remote.isPermissionDenied
import com.evoapps.evoshift.data.remote.safeSnapshots
import com.evoapps.evoshift.domain.model.ChecklistCheck
import com.evoapps.evoshift.domain.model.ChecklistItem
import com.evoapps.evoshift.domain.model.ChecklistKind
import com.evoapps.evoshift.domain.model.ChecklistTemplate
import com.evoapps.evoshift.domain.model.ShiftChecklist
import com.evoapps.evoshift.domain.repository.ChecklistRepository
import com.evoapps.evoshift.data.remote.AppFirestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.map
import kotlinx.serialization.Serializable

/** `workplaces/{id}/checklist_templates/{templateId}` (SH-121); read by functions/checklists.js. */
@Serializable
internal data class ChecklistTemplateDto(
    val id: String = "",
    val workplaceId: String = "",
    val name: String = "",
    val kind: String = "CUSTOM",
    val items: List<ChecklistItemDto> = emptyList(),
    val shiftTemplateIds: List<String> = emptyList(),
    /** ISO days of week, 1 = Monday. */
    val weekdays: List<Int> = emptyList(),
    val updatedAt: String? = null,
    val updatedBy: String? = null
)

@Serializable
internal data class ChecklistItemDto(val id: String = "", val text: String = "", val requiresNote: Boolean = false)

@Serializable
internal data class ChecklistCheckDto(val done: Boolean = false, val note: String? = null, val at: String? = null)

/** `shift_checklists/{shiftId}_{templateId}` (SH-121): the shift's person writes it (firestore.rules). */
@Serializable
internal data class ShiftChecklistDto(
    val shiftId: String = "",
    val templateId: String = "",
    val workplaceId: String = "",
    val rosterId: String = "",
    val date: String = "",
    val userId: String = "",
    val name: String = "",
    val kind: String = "CUSTOM",
    val items: List<ChecklistItemDto> = emptyList(),
    val checks: Map<String, ChecklistCheckDto> = emptyMap(),
    val completedAt: String? = null,
    val updatedAt: String? = null,
    /** SH-127: written only by kioskChecklist; the person's saves carry it along unchanged. */
    val skippedAt: String? = null,
    val skippedVia: String? = null
)

internal fun ChecklistItemDto.toDomain() = ChecklistItem(id, text, requiresNote)
internal fun ChecklistItem.toDto() = ChecklistItemDto(id, text, requiresNote)

internal fun ChecklistTemplateDto.toDomain(docId: String) = ChecklistTemplate(
    id = id.ifBlank { docId },
    workplaceId = workplaceId,
    name = name,
    kind = ChecklistKind.fromName(kind),
    items = items.map { it.toDomain() },
    shiftTemplateIds = shiftTemplateIds,
    weekdays = weekdays,
    updatedAt = updatedAt,
    updatedBy = updatedBy
)

internal fun ChecklistTemplate.toDto() = ChecklistTemplateDto(
    id = id,
    workplaceId = workplaceId,
    name = name,
    kind = kind.name,
    items = items.map { it.toDto() },
    shiftTemplateIds = shiftTemplateIds,
    weekdays = weekdays,
    updatedAt = updatedAt,
    updatedBy = updatedBy
)

internal fun ShiftChecklistDto.toDomain() = ShiftChecklist(
    shiftId = shiftId,
    templateId = templateId,
    workplaceId = workplaceId,
    rosterId = rosterId,
    date = date,
    userId = userId,
    name = name,
    kind = ChecklistKind.fromName(kind),
    items = items.map { it.toDomain() },
    checks = checks.mapValues { (_, c) -> ChecklistCheck(c.done, c.note, c.at) },
    completedAt = completedAt,
    updatedAt = updatedAt,
    skippedAt = skippedAt,
    skippedVia = skippedVia
)

internal fun ShiftChecklist.toDto() = ShiftChecklistDto(
    shiftId = shiftId,
    templateId = templateId,
    workplaceId = workplaceId,
    rosterId = rosterId,
    date = date,
    userId = userId,
    name = name,
    kind = kind.name,
    items = items.map { it.toDto() },
    checks = checks.mapValues { (_, c) -> ChecklistCheckDto(c.done, c.note, c.at) },
    completedAt = completedAt,
    updatedAt = updatedAt,
    skippedAt = skippedAt,
    skippedVia = skippedVia
)

class ChecklistRepositoryImpl(private val firestore: AppFirestore) : ChecklistRepository {
    private fun templates(workplaceId: String) =
        firestore.collection("workplaces").document(workplaceId).collection("checklist_templates")

    private val checklists get() = firestore.collection("shift_checklists")

    override fun observeTemplates(workplaceId: String): Flow<List<ChecklistTemplate>> =
        templates(workplaceId).safeSnapshots
            .map { snapshot ->
                snapshot.documents
                    .mapNotNull { doc -> runCatching { doc.data<ChecklistTemplateDto>().toDomain(doc.id) }.getOrNull() }
                    .sortedWith(compareBy<ChecklistTemplate>({ it.kind.ordinal }, { it.name }))
            }
            .distinctUntilChanged()

    override suspend fun saveTemplate(template: ChecklistTemplate): Result<String> = runCatching {
        val ref = if (template.id.isBlank()) templates(template.workplaceId).document else templates(template.workplaceId).document(template.id)
        ref.set(template.copy(id = ref.id).toDto())
        ref.id
    }

    override suspend fun deleteTemplate(workplaceId: String, templateId: String): Result<Unit> = runCatching {
        templates(workplaceId).document(templateId).delete()
    }

    override fun observeDay(workplaceId: String, date: String): Flow<List<ShiftChecklist>> =
        checklists
            .where { "workplaceId" equalTo workplaceId }
            .where { "date" equalTo date }
            .snapshots
            .map { snapshot -> snapshot.documents.mapNotNull { runCatching { it.data<ShiftChecklistDto>().toDomain() }.getOrNull() } }
            // Not a manager here (any more): nothing to show rather than an error.
            .catch { e -> if (e.isPermissionDenied()) emit(emptyList()) else throw e }
            .distinctUntilChanged()

    override fun observeForShift(userId: String, shiftId: String): Flow<List<ShiftChecklist>> =
        checklists
            .where { "userId" equalTo userId }
            .where { "shiftId" equalTo shiftId }
            .safeSnapshots
            .map { snapshot -> snapshot.documents.mapNotNull { runCatching { it.data<ShiftChecklistDto>().toDomain() }.getOrNull() } }
            .distinctUntilChanged()

    override suspend fun save(checklist: ShiftChecklist): Result<Unit> = runCatching {
        checklists.document(checklist.id).set(checklist.toDto())
    }
}
